import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { marker as _ } from '@jsverse/transloco-keys-manager/marker';
import { EMPTY, finalize, first, map } from 'rxjs';
import { DeploymentInstanceResponse, DeploymentsClient } from '../../../shared';
import { LoadingDefaults } from '../../../shared/models/loading-defaults.model';
import { RplLoadingService } from '../../../shared/services/rpl-loading.service';

export const deploymentInstanceResolver: ResolveFn<
  DeploymentInstanceResponse
> = (route: ActivatedRouteSnapshot) => {
  const client = inject(DeploymentsClient);
  const loadingService = inject(RplLoadingService);
  const router = inject(Router);
  const snackbar = inject(MatSnackBar);
  const translocoService = inject(TranslocoService);

  const deploymentId = Number(route.paramMap.get('deploymentId'));
  const instanceId = Number(route.paramMap.get('deploymentInstanceId'));
  const isEditRoute = route.routeConfig?.path === 'edit';

  if (Number.isNaN(deploymentId)) {
    void router.navigate(['deployments']);
    return EMPTY;
  }

  let loadingId: string | undefined;
  const timeout = setTimeout(() => {
    loadingId = loadingService.start();
  }, LoadingDefaults.StartIndicatorTimeout);

  return client.getDeploymentInstanceById(deploymentId, instanceId).pipe(
    first(),
    map((response) => {
      if (isEditRoute && response.isReadOnly) {
        snackbar.open(
          translocoService.translate(
            _('i18n.DeploymentInstance.Edit.ReadOnly'),
            {
              name: response.name,
            },
          ),
          translocoService.translate(_('i18n.Common.Button.Ok')),
        );

        void router.navigate([
          'deployments',
          deploymentId,
          'instances',
          instanceId,
          'detail',
        ]);
      }

      return response;
    }),
    finalize(() => {
      clearTimeout(timeout);
      loadingService.end(loadingId);
    }),
  );
};
